import * as Yup from 'yup';
import { EMAIL_REGEX, isValidMobileNumber, extractNationalNumber } from 'utils';
import { shouldEnforceFieldAsMandatory } from 'utils/skippableFieldsUtils';
import { WorkerProfileField } from 'types/profileCustomFields';
import store from 'modules/state-mgmt/store';
import { shouldShowStateForCountry } from 'utils/addressValidationUtils';

// Helper function to create phone validation that uses skippable field logic
const createSkippablePhoneValidation = (
  workerProfileField: WorkerProfileField,
  profileFieldConstraintsMap: any,
  hasReachedConsentForm: boolean,
  requiredErrorMessage: string,
  invalidErrorMessage: string,
) => {
  return Yup.string().test('phone-validation', invalidErrorMessage, function (value) {
    const nationalNumber = extractNationalNumber(value);
    const isMandatory = shouldEnforceFieldAsMandatory(
      workerProfileField,
      profileFieldConstraintsMap,
      hasReachedConsentForm,
    );

    // If field is mandatory and no national number, show required error
    if (isMandatory && (!nationalNumber || nationalNumber.trim() === '')) {
      return this.createError({ message: requiredErrorMessage });
    }

    // If field is not mandatory and no national number, it's valid
    if (!isMandatory && (!nationalNumber || nationalNumber.trim() === '')) {
      return true;
    }

    // If there's a national number, validate the full phone number format
    if (nationalNumber && nationalNumber.trim() !== '') {
      try {
        const isValid = isValidMobileNumber(value);
        return isValid;
      } catch (error: any) {
        return false;
      }
    }

    return true;
  });
};

// Helper function to create phone validation that checks the national number
const createPhoneValidation = (
  fieldConfig: { isMandatory?: boolean },
  requiredErrorMessage: string,
  invalidErrorMessage: string,
) => {
  return Yup.string().test('phone-validation', invalidErrorMessage, function (value) {
    const isMandatory = fieldConfig?.isMandatory;
    const nationalNumber = extractNationalNumber(value);

    // If field is mandatory and no national number, show required error
    if (isMandatory && (!nationalNumber || nationalNumber.trim() === '')) {
      return this.createError({ message: requiredErrorMessage });
    }

    // If field is not mandatory and no national number, it's valid
    if (!isMandatory && (!nationalNumber || nationalNumber.trim() === '')) {
      return true;
    }

    // If there's a national number, validate the full phone number format
    if (nationalNumber && nationalNumber.trim() !== '') {
      try {
        const isValid = isValidMobileNumber(value);
        return isValid;
      } catch (error: any) {
        return false;
      }
    }

    return true;
  });
};

// Helper function to create either/or validation for email/phone
const createEitherOrValidation = (fieldType: 'email' | 'phone', errorMessage: string) => {
  return function (this: Yup.TestContext<any>, value: string) {
    if (fieldType === 'email') {
      const mobileNumber = this.parent.mobileNumber;
      const nationalNumber = extractNationalNumber(mobileNumber);
      const hasEmail = value && value.trim() !== '';
      const hasPhone = nationalNumber && nationalNumber.trim() !== '';

      return hasEmail || hasPhone;
    } else {
      const email = this.parent.email;
      const mobileNumber = this.parent.mobileNumber;
      const nationalNumber = extractNationalNumber(mobileNumber);
      const hasPhone = nationalNumber && nationalNumber.trim() !== '';
      const hasEmail = email && email.trim() !== '';

      return hasPhone || hasEmail;
    }
  };
};

const contactInformationSchema = (
  sectionsConfig: any,
  t: (key: string) => string,
  profileFieldConstraintsMap: any,
  hasReachedConsentForm: boolean,
) => {
  return Yup.object({
    // Contact Information
    email: Yup.string()
      .matches(EMAIL_REGEX, {
        message: t('contactInformation.errors.emailInvalid'),
        excludeEmptyString: true,
      })
      .when([], {
        is: () => sectionsConfig.contactInfo.email.isVisible,
        then: schema => {
          let emailSchema = schema.email(t('contactInformation.errors.emailInvalid'));

          // Primary email is always mandatory when required (cannot be skippable)
          if (sectionsConfig.contactInfo.email.isMandatory && !sectionsConfig.contactInfo.isEitherRequired) {
            emailSchema = emailSchema.required(t('contactInformation.errors.emailRequired'));
          }

          // Either/or validation
          if (sectionsConfig.contactInfo.isEitherRequired) {
            emailSchema = emailSchema.test(
              'email-or-phone-required',
              t('contactInformation.errors.contactMethodRequired'),
              createEitherOrValidation('email', t('contactInformation.errors.contactMethodRequired')),
            );
          }

          return emailSchema;
        },
        otherwise: schema => schema.notRequired(),
      }),

    mobileNumber: Yup.string().when([], {
      is: () => sectionsConfig.contactInfo.phone?.isVisible,
      then: schema => {
        let phoneSchema = createPhoneValidation(
          sectionsConfig.contactInfo.phone,
          t('contactInformation.errors.phoneRequired'),
          t('contactInformation.errors.phoneInvalid'),
        );

        // Either/or validation for primary phone
        if (sectionsConfig.contactInfo.isEitherRequired) {
          phoneSchema = phoneSchema.test(
            'email-or-phone-required',
            t('contactInformation.errors.contactMethodRequired'),
            createEitherOrValidation('phone', t('contactInformation.errors.contactMethodRequired')),
          );
        }

        return phoneSchema;
      },
      otherwise: schema => schema.notRequired(),
    }),

    phoneNumber: Yup.string().when([], {
      is: () => sectionsConfig.contactInfo.alternatePhone?.isVisible,
      then: () =>
        createSkippablePhoneValidation(
          WorkerProfileField.alternateContactMobilePhoneNumber,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
          t('contactInformation.errors.alternatePhoneRequired'),
          t('contactInformation.errors.phoneInvalid'),
        ),
      otherwise: schema => schema.notRequired(),
    }),

    addressLine1: Yup.string().when([], {
      is: () => sectionsConfig.homeAddress.addressLine1.isVisible,
      then: schema => {
        const shouldEnforceField = shouldEnforceFieldAsMandatory(
          WorkerProfileField.homeAddressLine1,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        );
        return shouldEnforceField
          ? schema.required(t('contactInformation.errors.addressLine1Required'))
          : schema.notRequired();
      },
      otherwise: schema => schema.notRequired(),
    }),

    addressLine2: Yup.string().when([], {
      is: () => sectionsConfig.homeAddress.addressLine2.isVisible,
      then: schema => {
        const shouldEnforceField = shouldEnforceFieldAsMandatory(
          WorkerProfileField.homeAddressLine2,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        );
        return shouldEnforceField
          ? schema.required(t('contactInformation.errors.addressLine2Required'))
          : schema.notRequired();
      },
      otherwise: schema => schema.notRequired(),
    }),

    country: Yup.string().when([], {
      is: () => sectionsConfig.homeAddress.country.isVisible,
      then: schema => {
        const shouldEnforceField = shouldEnforceFieldAsMandatory(
          WorkerProfileField.homeAddressCountry,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        );
        return shouldEnforceField
          ? schema.required(t('contactInformation.errors.countryRequired'))
          : schema.notRequired();
      },
      otherwise: schema => schema.notRequired(),
    }),

    state: Yup.string().test('state-required', t('contactInformation.errors.stateRequired'), function (value) {
      // Check if state section is configured to be visible and if it should be enforced
      const isVisible = sectionsConfig.homeAddress.state.isVisible;
      const shouldEnforceField = shouldEnforceFieldAsMandatory(
        WorkerProfileField.homeAddressStateProvince,
        profileFieldConstraintsMap,
        hasReachedConsentForm,
      );

      if (!isVisible || !shouldEnforceField) {
        return true; // Not required by configuration or skippable logic
      }

      // Get the selected country UUID from form context
      const { country: countryUUID } = this.parent;

      // If no country is selected, state is not required
      if (!countryUUID) {
        return true;
      }

      // Get countries from Redux store to find the country code
      const storeState = store.getState();
      const language = storeState?.app?.languageCode || 'en';
      const countries = storeState?.cache?.countries?.[language] || [];

      // Find the selected country by UUID to get its code
      const selectedCountry = countries.find((c: any) => c.id === countryUUID);

      if (!selectedCountry) {
        return true; // If country not found, don't require state
      }

      // Check if this country requires states using the shouldShowStateForCountry utility
      const countryRequiresState = shouldShowStateForCountry(selectedCountry.code);

      // If country doesn't require state (not US, CA, or MX), state is not required
      if (!countryRequiresState) {
        return true;
      }

      // If country requires state and field should be enforced, check if value exists
      if (!value || value.trim() === '') {
        return false; // State is required but not provided
      }

      return true; // State is provided
    }),

    city: Yup.string().when([], {
      is: () => sectionsConfig.homeAddress.city.isVisible,
      then: schema => {
        const shouldEnforceField = shouldEnforceFieldAsMandatory(
          WorkerProfileField.homeAddressCity,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        );
        return shouldEnforceField ? schema.required(t('contactInformation.errors.cityRequired')) : schema.notRequired();
      },
      otherwise: schema => schema.notRequired(),
    }),

    zipCode: Yup.string().when([], {
      is: () => sectionsConfig.homeAddress.zipCode.isVisible,
      then: schema => {
        const shouldEnforceField = shouldEnforceFieldAsMandatory(
          WorkerProfileField.homeAddressZipPostal,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        );
        return shouldEnforceField
          ? schema.required(t('contactInformation.errors.zipCodeRequired'))
          : schema.notRequired();
      },
      otherwise: schema => schema.notRequired(),
    }),

    county: Yup.string(), // County is typically optional
    borough: Yup.string(), // NYC Borough is optional and only shown for New York state
    latitude: Yup.number().notRequired(), // Latitude from autocomplete
    longitude: Yup.number().notRequired(), // Longitude from autocomplete

    // Emergency Contact
    emergencyContactName: Yup.string().when([], {
      is: () => sectionsConfig.emergencyContact.emergencyContactName.isVisible,
      then: schema => {
        const shouldEnforceField = shouldEnforceFieldAsMandatory(
          WorkerProfileField.emergencyContactName,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        );
        return shouldEnforceField
          ? schema.required(t('contactInformation.errors.emergencyContactNameRequired'))
          : schema.notRequired();
      },
      otherwise: schema => schema.notRequired(),
    }),

    // Emergency contact phone - simplified using helper
    emergencyContactNumber: Yup.string().when([], {
      is: () => sectionsConfig.emergencyContact.emergencyContactNumber.isVisible,
      then: () =>
        createSkippablePhoneValidation(
          WorkerProfileField.emergencyContactMobilePhone,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
          t('contactInformation.errors.emergencyContactNumberRequired'),
          t('contactInformation.errors.phoneInvalid'),
        ),
      otherwise: schema => schema.notRequired(),
    }),

    emergencyContactRelationship: Yup.string().when([], {
      is: () => sectionsConfig.emergencyContact.emergencyContactRelationship.isVisible,
      then: schema => {
        const shouldEnforceField = shouldEnforceFieldAsMandatory(
          WorkerProfileField.emergencyContactRelationship,
          profileFieldConstraintsMap,
          hasReachedConsentForm,
        );
        return shouldEnforceField
          ? schema.required(t('contactInformation.errors.emergencyContactRelationshipRequired'))
          : schema.notRequired();
      },
      otherwise: schema => schema.notRequired(),
    }),

    // Additional address fields for API structure (internal fields, not validated)
    countryName: Yup.string().notRequired(),
    stateName: Yup.string().notRequired(),
  });
};

export default contactInformationSchema;
